/** Geometry for the sign-in panel's ruled silk: the ruled lines of a page, swelling like cloth on water. */

export type Field = {
  readonly width: number;
  readonly height: number;
  /** Distance between two rules at rest. */
  readonly gap: number;
  /** Rules drawn, one spare past each edge so a swell never uncovers a seam. */
  readonly rows: number;
  /** Horizontal distance between the points of a rule. */
  readonly step: number;
};

export function layoutField(width: number, height: number): Field {
  const gap = Math.min(16, Math.max(10, height / 64));
  return {
    width,
    height,
    gap,
    rows: height > 0 ? Math.floor(height / gap) + 3 : 0,
    step: Math.max(6, width / 120),
  };
}

export const rowBase = (field: Field, row: number): number => (row - 1) * field.gap;

/** How far a rule swells: calm at the top and bottom, deepest a little above the middle. */
export function swell(v: number): number {
  const d = (v - 0.42) / 0.3;
  return 0.25 + 0.75 * Math.exp(-d * d);
}

export type Pointer = { x: number; y: number; presence: number };

/**
 * The vertical offset of a rule resting at `y0`, at `x` and field time `t`
 * (seconds). Three slow waves whose phase barely changes from one rule to the
 * next, so neighbours move together like cloth; the pointer parts the rules
 * around it, and `presence` (0–1) fades that in and out.
 */
export function displace(field: Field, x: number, y0: number, t: number, pointer: Pointer): number {
  const u = x / field.width;
  const v = y0 / field.height;
  const wave =
    0.55 * Math.sin(u * 5.1 + v * 2.3 + t * 0.42) +
    0.3 * Math.sin(u * 9.7 - v * 4.1 - t * 0.61 + 1.7) +
    0.15 * Math.sin((u + v) * 15.3 + t * 0.93 + 4.2);
  let dy = field.height * 0.022 * swell(v) * wave;
  if (pointer.presence > 0) {
    const dx = x - pointer.x;
    const dp = y0 + dy - pointer.y;
    const r = field.height * 0.12;
    const falloff = Math.exp(-(dx * dx + dp * dp) / (r * r));
    dy += field.gap * 2.2 * falloff * pointer.presence * Math.tanh(dp / (field.gap * 1.5));
  }
  return dy;
}
